import Button from 'components/features/pfm/Button/Button'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { OnboardingPagePreset } from 'components/features/creditCard/creditCardOnboarding/OnboardingPagePreset/OnboardingPagePreset'
import styles from 'components/features/creditCard/creditCardOnboarding/OfferStepPage/OfferStepPage.styl'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { cryptoMetaGuard } from 'components/features/rewards/rewardsPane/guards/cryptoMetaGuard'
import { formatPercent, formatUSCurrency } from 'helpers/formatHelpers'
import { CCOnboardingAcceptAction, PatchCCOnboardingPropsAction } from 'components/features/creditCard/creditCardStore/actions'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import moment from 'moment'
import { CARD_AGREEMENT_PDF_URL } from 'components/features/creditCard/creditCardStore/constants'

const EMPTY_OBJ = {}

export const OfferStepPage: FC = () => {
  useSimpleGuard(cryptoMetaGuard, EMPTY_OBJ, 'OfferStepPage')

  const offer = useStoreSelector((state) => state.data.creditCard.onboarding?.offer ?? null)

  const dispatch = useStoreDispatch()
  useEffect(() => {
    if (!offer) {
      return
    }

    const remain = moment(offer.deadline).diff().valueOf()

    const tmr = window.setTimeout(() => {
      dispatch(new PatchCCOnboardingPropsAction({
        step: 'address',
      }))
    }, remain)

    return () => {
      window.clearTimeout(tmr)
    }
  }, [dispatch, offer])

  const cardChild = useMemo(() => {
    if (!offer) {
      return (
        <LoadingOverlay />
      )
    }

    return (
      <>
        <div data-qa-marker='offer-card'>
          <div className={ styles.item } data-qa-marker='cash-back-item'>
            <div className={ styles.label } data-qa-type='label'>
              Cash back
            </div>
            <div className={ styles.value } data-qa-type='value'>
              { formatPercent(+offer.cashback / 100, 'as-is') } on all purchases
            </div>
          </div>

          <div className={ styles.item } data-qa-marker='fee-item'>
            <div className={ styles.label } data-qa-type='label'>
              Annual Fee
            </div>
            <div className={ styles.value } data-qa-type='value'>
              { formatUSCurrency(offer.annual_fee) }
            </div>
          </div>

          <div className={ styles.item } data-qa-marker='apr-item'>
            <div className={ styles.label } data-qa-type='label'>
              APR
            </div>
            <div className={ styles.value } data-qa-type='value'>
              { +offer.retail_apr + '%' }
            </div>
          </div>

          <div className={ styles.item } data-qa-marker='credit-limit-item'>
            <div className={ styles.label } data-qa-type='label'>
              Credit Limit
            </div>
            <div className={ styles.value } data-qa-type='value'>
              { formatUSCurrency(offer.credit_limit) }
            </div>
          </div>

        </div>

        <div className={ styles.note } data-qa-marker='form-note'>
          View your offer, customer agreement, credit score notice, and APR details from Evolve Bank.
        </div>

        <div className={ styles.termsLink }>
          <a href={ CARD_AGREEMENT_PDF_URL } target='_blank' data-qa-marker='card-terms-link'>Card Agreement</a>
        </div>
      </>
    )
  }, [offer])

  const handleAccept = useCallback(() => {
    dispatch(new CCOnboardingAcceptAction())
  }, [dispatch])

  const handleDecline = useCallback(() => {
    dispatch(new PatchCCOnboardingPropsAction({
      step: 'offer-declined',
    }))
  }, [dispatch])

  const isLoading = useStoreSelector((state) => state.app.creditCard.acceptRequest.isLoading)
  const isDeclineLoading = useStoreSelector((state) => state.app.creditCard.patchOnboardingPropsRequest.isLoading)

  const buttonsChild = useMemo(() => !!offer && (
    <div className={ styles.preset }>
      <Button
        text='Accept Offer'
        onClick={ handleAccept }
        qaMarker='accept-offer'
        isSubmitting={ isLoading }
      />
      <Button
        text='Decline Offer'
        style='textOnly'
        qaMarker='decline-offer'
        onClick={ handleDecline }
        isSubmitting={ isDeclineLoading }
      />
    </div>
  ), [handleAccept, handleDecline, isDeclineLoading, isLoading, offer])

  return (
    <OnboardingPagePreset
      step={ 9 }
      title={ <span>Your application was <br />pre-approved</span> }
      card={ cardChild }
      progressPercent={ 80 }
    >
      { buttonsChild }
    </OnboardingPagePreset>
  )
}
